/**
 * Notification Service for V2 Services
 */

import { FirestoreService } from '../db/FirestoreService';
import { Notification, NotificationData, NotificationType, NotificationPriority } from '../models/Notification.model';

export class NotificationService {
  private firestore: FirestoreService;
  private readonly COLLECTION = 'notifications';

  constructor() {
    this.firestore = FirestoreService.getInstance();
  }

  async createNotification(notificationData: Partial<NotificationData>): Promise<Notification> {
    const notification = new Notification(notificationData);
    const docRef = await this.firestore.addDocument(this.COLLECTION, notification.toFirestore());
    notification.id = docRef.id;
    return notification;
  }

  async getNotification(id: string): Promise<Notification | null> {
    const doc = await this.firestore.getDocument(this.COLLECTION, id);
    return doc ? Notification.fromFirestore({ id, ...doc }) : null;
  }

  async updateNotification(id: string, updates: Partial<NotificationData>): Promise<void> {
    await this.firestore.updateDocument(this.COLLECTION, id, updates);
  }

  async deleteNotification(id: string): Promise<void> {
    await this.firestore.deleteDocument(this.COLLECTION, id);
  }

  async markAsRead(id: string): Promise<void> {
    const notification = await this.getNotification(id);
    if (notification) {
      notification.markAsRead();
      await this.updateNotification(id, notification.toFirestore());
    }
  }

  async markAsUnread(id: string): Promise<void> {
    const notification = await this.getNotification(id);
    if (notification) {
      notification.markAsUnread();
      await this.updateNotification(id, notification.toFirestore());
    }
  }

  async getUserNotifications(userId: string, limit?: number): Promise<Notification[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'userId', operator: '==', value: userId }],
      { field: 'createdAt', direction: 'desc' },
      limit
    );
    return docs.map(doc => Notification.fromFirestore(doc));
  }

  async getUnreadNotifications(userId: string): Promise<Notification[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [
        { field: 'userId', operator: '==', value: userId },
        { field: 'read', operator: '==', value: false }
      ],
      { field: 'createdAt', direction: 'desc' }
    );
    return docs.map(doc => Notification.fromFirestore(doc));
  }

  async getNotificationsByType(type: NotificationType, limit?: number): Promise<Notification[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'type', operator: '==', value: type }],
      { field: 'createdAt', direction: 'desc' },
      limit
    );
    return docs.map(doc => Notification.fromFirestore(doc));
  }

  async getNotificationsByPriority(priority: NotificationPriority, limit?: number): Promise<Notification[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'priority', operator: '==', value: priority }],
      { field: 'createdAt', direction: 'desc' },
      limit
    );
    return docs.map(doc => Notification.fromFirestore(doc));
  }

  async getAllNotifications(limit?: number): Promise<Notification[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [],
      { field: 'createdAt', direction: 'desc' },
      limit
    );
    return docs.map(doc => Notification.fromFirestore(doc));
  }

  async getUnreadCount(userId: string): Promise<number> {
    const unreadNotifications = await this.getUnreadNotifications(userId);
    return unreadNotifications.length;
  }

  async markAllAsRead(userId: string): Promise<void> {
    const unreadNotifications = await this.getUnreadNotifications(userId);
    const updatePromises = unreadNotifications.map(notification =>
      this.markAsRead(notification.id!)
    );
    await Promise.all(updatePromises);
  }

  async deleteOldNotifications(daysOld: number = 30): Promise<number> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'createdAt', operator: '<', value: cutoffDate }]
    );

    const deletePromises = docs.map(doc => this.deleteNotification(doc.id));
    await Promise.all(deletePromises);

    return docs.length;
  }

  async sendBookingNotification(
    userId: string,
    bookingId: string,
    type: 'booking_created' | 'booking_scheduled' | 'booking_completed' | 'booking_cancelled',
    customMessage?: string
  ): Promise<Notification> {
    const messages = {
      booking_created: 'Your cleaning booking has been created',
      booking_scheduled: 'Your cleaning has been scheduled',
      booking_completed: 'Your cleaning has been completed',
      booking_cancelled: 'Your booking has been cancelled'
    };

    const titles = {
      booking_created: 'Booking Created',
      booking_scheduled: 'Cleaning Scheduled',
      booking_completed: 'Cleaning Complete',
      booking_cancelled: 'Booking Cancelled'
    };

    return this.createNotification({
      userId,
      type,
      title: titles[type],
      message: customMessage || messages[type],
      priority: type === 'booking_cancelled' ? 'high' : 'medium',
      metadata: { bookingId },
      actionUrl: `/bookings/${bookingId}`
    });
  }

  async sendPaymentNotification(
    userId: string,
    amount: number,
    success: boolean,
    paymentIntentId?: string
  ): Promise<Notification> {
    const type: NotificationType = success ? 'payment_received' : 'payment_failed';
    const title = success ? 'Payment Received' : 'Payment Failed';
    const message = success
      ? `Payment of $${amount} has been processed successfully`
      : `Payment of $${amount} failed. Please try again.`;

    return this.createNotification({
      userId,
      type,
      title,
      message,
      priority: success ? 'medium' : 'high',
      metadata: { amount, paymentIntentId }
    });
  }

  async sendRobotNotification(
    userId: string,
    robotId: string,
    type: 'robot_assigned' | 'robot_maintenance'
  ): Promise<Notification> {
    const messages = {
      robot_assigned: 'A robot has been assigned to your cleaning',
      robot_maintenance: 'Robot maintenance scheduled - your cleaning may be rescheduled'
    };

    const titles = {
      robot_assigned: 'Robot Assigned',
      robot_maintenance: 'Maintenance Notice'
    };

    return this.createNotification({
      userId,
      type,
      title: titles[type],
      message: messages[type],
      priority: type === 'robot_maintenance' ? 'high' : 'medium',
      metadata: { robotId }
    });
  }

  async sendSystemAlert(
    message: string,
    priority: NotificationPriority = 'medium',
    userIds?: string[]
  ): Promise<Notification[]> {
    if (!userIds) {
      const userService = (await import('./UserService')).UserService;
      const users = new userService();
      const allUsers = await users.getAllUsers();
      userIds = allUsers.map(user => user.uid);
    }

    const notifications = await Promise.all(
      userIds.map(userId =>
        this.createNotification({
          userId,
          type: 'system_alert',
          title: 'System Alert',
          message,
          priority
        })
      )
    );

    return notifications;
  }

  subscribeToUserNotifications(userId: string, callback: (notifications: Notification[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.COLLECTION,
      (docs) => {
        const notifications = docs.map(doc => Notification.fromFirestore(doc));
        callback(notifications);
      },
      [{ field: 'userId', operator: '==', value: userId }],
      { field: 'createdAt', direction: 'desc' }
    );
  }

  subscribeToAllNotifications(callback: (notifications: Notification[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.COLLECTION,
      (docs) => {
        const notifications = docs.map(doc => Notification.fromFirestore(doc));
        callback(notifications);
      },
      [],
      { field: 'createdAt', direction: 'desc' }
    );
  }
}